/**
 * 테마에 appearance(브랜드 슬롯·모드) 적용
 */

import { theme as baseTheme } from 'styles/theme';
import { softenBrand, deriveBrandSlots, normalizeHex } from 'styles/brandColorUtils';
import { DARK_SURFACE_OVERRIDES, DARK_SHADOW_OVERRIDES, adaptBrandSlotsForDark } from 'styles/darkSurface';
import { defaultAppearanceSettings, DEFAULT_BRAND_MAIN } from 'styles/appearanceSlots';
import { fontFamilyStack, normalizeFontSizeRootPx } from 'styles/erpFonts';

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function deepMerge(target, source) {
  if (!source || typeof source !== 'object') return target;
  const out = { ...target };
  Object.keys(source).forEach((key) => {
    const sv = source[key];
    if (sv && typeof sv === 'object' && !Array.isArray(sv)) {
      out[key] = deepMerge(target[key] && typeof target[key] === 'object' ? target[key] : {}, sv);
    } else if (sv !== undefined) {
      out[key] = sv;
    }
  });
  return out;
}

/**
 * 슬롯 맵 → theme.colors 경로 반영
 * @param {typeof baseTheme} t
 * @param {Record<string, string>} slots
 */
export function applySlotsToTheme(t, slots) {
  if (!slots) return t;
  const c = t.colors;
  if (slots.primary) {
    c.primary = slots.primary;
    c.background.primary = slots.primary;
    c.blue = slots.primary;
  }
  if (slots.primaryDark) c.primaryDark = slots.primaryDark;
  if (slots.primaryLight) c.primaryLight = slots.primaryLight;
  if (slots.focusRing) c.focusRing = slots.focusRing;
  if (slots.textLink) c.text.link = slots.textLink;
  if (slots.statusInfo) c.status.info = slots.statusInfo;
  if (slots.gridHeaderActive) {
    c.grid.headerActiveBg = slots.gridHeaderActive;
    c.grid.headerAccent = slots.gridHeaderAccent || slots.gridHeaderActive;
  }
  if (slots.gridHeaderActiveHover) c.grid.headerActiveHover = slots.gridHeaderActiveHover;
  if (slots.gridHeaderActiveFg) c.grid.headerActiveFg = slots.gridHeaderActiveFg;
  if (slots.gridHeaderActiveSort) c.grid.headerActiveSort = slots.gridHeaderActiveSort;
  if (slots.gridHeaderAccent) c.grid.headerAccent = slots.gridHeaderAccent;
  if (slots.gridRowSelected) c.grid.rowSelected = slots.gridRowSelected;
  if (slots.gridRowHover) c.grid.rowHover = slots.gridRowHover;
  if (slots.gridCellSelected) c.grid.cellSelected = slots.gridCellSelected;
  if (slots.gridCellBlockBorder) c.grid.cellBlockBorder = slots.gridCellBlockBorder;
  if (slots.gridCellFocused) c.grid.cellFocused = slots.gridCellFocused;
  return t;
}

/**
 * appearance 설정 → 최종 theme 객체
 * @param {import('styles/appearanceSlots').AppearanceSettings} appearance
 * @param {typeof baseTheme} [base]
 */
export function buildThemeFromAppearance(appearance, base = baseTheme) {
  const cfg = { ...defaultAppearanceSettings(), ...(appearance || {}) };
  const mainNorm = (normalizeHex(cfg.mainHex) || '').toUpperCase();
  const sourceNorm = (normalizeHex(cfg.sourceHex) || '').toUpperCase();
  const defaultNorm = DEFAULT_BRAND_MAIN.toUpperCase();
  const hasOverrides =
    cfg.slotOverrides && typeof cfg.slotOverrides === 'object' && Object.keys(cfg.slotOverrides).length > 0;
  // 시스템 기본(#2563EB)만이면 soft 재파생하지 않아 기존 UI 톤을 유지
  const hasCustomBrand =
    hasOverrides ||
    (mainNorm && mainNorm !== defaultNorm) ||
    (sourceNorm && sourceNorm !== defaultNorm);

  let soft;
  if (hasCustomBrand) {
    soft = softenBrand(cfg.mainHex || cfg.sourceHex || DEFAULT_BRAND_MAIN);
  } else {
    soft = {
      brandBase: base.colors.primary,
      softened: false,
      sourceHex: base.colors.primary,
    };
  }

  let slots = hasCustomBrand
    ? deriveBrandSlots(soft.brandBase)
    : {
        primary: base.colors.primary,
        primaryDark: base.colors.primaryDark,
        primaryLight: base.colors.primaryLight,
        focusRing: base.colors.focusRing,
        gridHeaderActive: base.colors.grid.headerActiveBg,
        gridHeaderActiveHover: base.colors.grid.headerActiveHover,
        gridHeaderActiveFg: base.colors.grid.headerActiveFg,
        gridHeaderActiveSort: base.colors.grid.headerActiveSort,
        gridHeaderAccent: base.colors.grid.headerAccent,
        gridRowSelected: base.colors.grid.rowSelected,
        gridRowHover: base.colors.grid.rowHover,
        gridCellSelected: base.colors.grid.cellSelected,
        gridCellBlockBorder: base.colors.grid.cellBlockBorder,
        gridCellFocused: base.colors.grid.cellFocused,
        textLink: base.colors.text.link,
        statusInfo: base.colors.status.info,
      };

  const overrides = cfg.slotOverrides && typeof cfg.slotOverrides === 'object' ? cfg.slotOverrides : {};
  Object.keys(overrides).forEach((k) => {
    const v = normalizeHex(overrides[k]) || overrides[k];
    if (v) slots[k] = v;
  });

  const mode = cfg.colorMode === 'dark' ? 'dark' : 'light';
  if (mode === 'dark') {
    slots = adaptBrandSlotsForDark(slots);
  }

  let next = deepClone(base);
  if (mode === 'dark') {
    next.colors = deepMerge(next.colors, DARK_SURFACE_OVERRIDES);
    next.shadows = { ...next.shadows, ...DARK_SHADOW_OVERRIDES };
  }
  applySlotsToTheme(next, slots);

  const fontFamilyId = cfg.fontFamilyId || 'pretendard';
  const fontSizeRootPx = normalizeFontSizeRootPx(cfg.fontSizeRootPx);
  const stack = fontFamilyStack(fontFamilyId);
  next.typography = {
    ...next.typography,
    fontFamily: stack,
    gridFontFamily: stack,
    fontSizeRootPx,
  };

  next.meta = {
    colorMode: mode,
    brandBase: soft.brandBase,
    sourceHex: soft.sourceHex,
    softened: soft.softened,
    slots,
    fontFamilyId,
    fontSizeRootPx,
  };
  return next;
}

/**
 * 기관·개인 appearance 머지
 * @param {import('styles/appearanceSlots').AppearanceSettings} tenant
 * @param {Partial<import('styles/appearanceSlots').AppearanceSettings>} [personal]
 */
export function mergeAppearance(tenant, personal) {
  const base = { ...defaultAppearanceSettings(), ...(tenant || {}) };
  const p = personal || {};
  const allowBrand = !!base.allowPersonalBrand;

  // 모드는 항상 개인 오버라이드 가능
  const colorMode = p.colorMode === 'dark' || p.colorMode === 'light' ? p.colorMode : base.colorMode;

  // 글자 크기: 접근성 — 기관이 허용하면 이 PC 값이 우선
  const allowFontSize = base.allowPersonalFontSize !== false;
  const fontSizeRootPx =
    allowFontSize && p.fontSizeRootPx != null
      ? normalizeFontSizeRootPx(p.fontSizeRootPx)
      : normalizeFontSizeRootPx(base.fontSizeRootPx);

  const mergedBase = {
    ...base,
    colorMode,
    fontFamilyId: base.fontFamilyId || 'pretendard',
    fontSizeRootPx,
  };

  if (!allowBrand) {
    return mergedBase;
  }

  return {
    ...mergedBase,
    mainHex: p.mainHex || base.mainHex,
    sourceHex: p.sourceHex || base.sourceHex,
    softened: p.softened != null ? p.softened : base.softened,
    slotOverrides: {
      ...(base.slotOverrides || {}),
      ...(p.slotOverrides || {}),
    },
  };
}
